/**
 * Mobile Menu – drill-down two-level navigation
 *
 * Level 1: flat list of top-level items (buttons for dropdowns, links for simple links)
 * Level 2: sub-screen with back button + title + content (accordions / featured cards)
 *
 * Content is derived from the already-rendered desktop .header__inline-menu DOM.
 */

(function () {
  'use strict';

  var OPEN_CLASS = 'mobile-menu-open';
  var OVERFLOW_CLASS = 'overflow-hidden-tablet';

  /* ── Init ──────────────────────────────────────────────────────────── */

  function init() {
    var toggle = document.querySelector('.js-mobile-menu-toggle');
    var overlay = document.getElementById('mobile-menu-overlay');
    var navContainer = document.getElementById('mobile-nav-container');

    if (!toggle || !overlay || !navContainer) return;

    buildNav(navContainer);

    toggle.addEventListener('click', function () {
      if (document.documentElement.classList.contains(OPEN_CLASS)) {
        closeMenu(navContainer, overlay, toggle);
      } else {
        openMenu(overlay, toggle);
      }
    });

    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && document.documentElement.classList.contains(OPEN_CLASS)) {
        closeMenu(navContainer, overlay, toggle);
        toggle.focus();
      }
    });

    // close when a link inside the overlay is tapped — the menu is how people navigate on mobile
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('a')) closeMenu(navContainer, overlay, toggle);
    });

    // never begin a page in a locked state (fresh load AND bfcache restore)
    window.addEventListener('pageshow', function () {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.body.classList.remove(OVERFLOW_CLASS);
    });

    // unlock if the viewport crosses the breakpoint while open
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 990) {
        closeMenu(navContainer, overlay, toggle);
      } else if (document.documentElement.classList.contains(OPEN_CLASS)) {
        // rotazione o cambio di altezza della barra annunci a menu aperto
        setOverlayTop();
      }
    });
  }

  /* L'overlay e' position:fixed, quindi il suo top va espresso rispetto al
     viewport. getBoundingClientRect().bottom dell'header e' gia' relativo al
     viewport e include la barra annunci con la sua altezza reale, comprese le
     righe in cui il testo va a capo. */
  function setOverlayTop() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--mobile-menu-top', bottom + 'px');
  }

  function openMenu(overlay, toggle) {
    setOverlayTop();
    document.documentElement.classList.add(OPEN_CLASS);
    document.body.classList.add(OVERFLOW_CLASS);
    overlay.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
  }

  function closeMenu(navContainer, overlay, toggle) {
    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.classList.remove(OVERFLOW_CLASS);
    overlay.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    // Reset to main screen if a sub-screen is open
    var activeScreen = navContainer.querySelector('.mobile-nav__screen--sub.is-active');
    if (activeScreen) {
      returnToMain(navContainer);
    }
  }

  /* ── Build navigation ──────────────────────────────────────────────── */

  function buildNav(container) {
    var mainScreen = document.createElement('div');
    mainScreen.className = 'mobile-nav__screen mobile-nav__screen--main';

    var mainList = document.createElement('ul');
    mainList.className = 'mobile-nav__list';
    mainScreen.appendChild(mainList);
    container.appendChild(mainScreen);

    var firstRight = true;

    ['left', 'right'].forEach(function (position) {
      var navs = document.querySelectorAll(
        '.header__inline-menu[data-position="' + position + '"]'
      );

      navs.forEach(function (nav) {
        var ul = nav.querySelector('ul');
        if (!ul) return;

        Array.from(ul.children).forEach(function (child) {
          var details = child.querySelector('details.mega-menu');
          var simpleLink = child.querySelector('a.header__menu-item');

          if (details) {
            var titleEl = details.querySelector('summary span');
            var title = titleEl ? titleEl.textContent.trim() : '';
            var megaContent = details.querySelector('.mega-menu__content');

            var subScreen = buildSubScreen(title, megaContent, container);
            container.appendChild(subScreen);

            var li = buildMainButton(title, position, firstRight && position === 'right', function () {
              openSubScreen(mainScreen, subScreen);
            });
            mainList.appendChild(li);
          } else if (simpleLink) {
            var li = buildMainLink(simpleLink, position, firstRight && position === 'right');
            mainList.appendChild(li);
          }

          if (position === 'right') firstRight = false;
        });
      });
    });
  }

  /* ── Main list items ───────────────────────────────────────────────── */

  function buildMainButton(title, position, addSeparator, onClick) {
    var li = document.createElement('li');
    li.className = 'mobile-nav__item' + (position === 'right' ? ' mobile-nav__item--secondary' : '');
    if (addSeparator) li.classList.add('mobile-nav__item--separator');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mobile-nav__btn' + (position === 'right' ? ' mobile-nav__btn--secondary' : '');

    var span = document.createElement('span');
    span.textContent = title;
    btn.appendChild(span);
    btn.appendChild(chevronRight());
    btn.addEventListener('click', onClick);

    li.appendChild(btn);
    return li;
  }

  function buildMainLink(sourceLink, position, addSeparator) {
    var li = document.createElement('li');
    li.className = 'mobile-nav__item' + (position === 'right' ? ' mobile-nav__item--secondary' : '');
    if (addSeparator) li.classList.add('mobile-nav__item--separator');

    var a = document.createElement('a');
    a.href = sourceLink.href;
    a.className = 'mobile-nav__btn mobile-nav__btn--link' +
      (position === 'right' ? ' mobile-nav__btn--secondary' : '');

    var sourceSpan = sourceLink.querySelector('span');
    a.textContent = sourceSpan ? sourceSpan.textContent.trim() : sourceLink.textContent.trim();

    li.appendChild(a);
    return li;
  }

  /* ── Sub-screen ────────────────────────────────────────────────────── */

  function buildSubScreen(title, megaContent, container) {
    var screen = document.createElement('div');
    screen.className = 'mobile-nav__screen mobile-nav__screen--sub';

    // Header row: back button + centered title
    var header = document.createElement('div');
    header.className = 'mobile-nav__sub-header';

    var backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'mobile-nav__back';
    backBtn.setAttribute('aria-label', 'Torna indietro');
    backBtn.appendChild(chevronLeft());
    backBtn.addEventListener('click', function () {
      returnToMain(container);
    });

    var titleEl = document.createElement('span');
    titleEl.className = 'mobile-nav__sub-title';
    titleEl.textContent = title;

    // Spacer to balance the back button so the title stays centered
    var spacer = document.createElement('span');
    spacer.className = 'mobile-nav__sub-spacer';
    spacer.setAttribute('aria-hidden', 'true');

    header.appendChild(backBtn);
    header.appendChild(titleEl);
    header.appendChild(spacer);
    screen.appendChild(header);

    if (megaContent) {
      screen.appendChild(buildSubContent(megaContent));
    }

    return screen;
  }

  /* ── Sub-screen content ────────────────────────────────────────────── */

  function buildSubContent(megaContent) {
    var wrapper = document.createElement('div');
    wrapper.className = 'mobile-nav__sub-content';

    var featuredLabel = megaContent.querySelector('.mega-menu__featured-label');
    var featuredLabelText = featuredLabel ? featuredLabel.textContent.trim() : null;

    var menuItems = megaContent.querySelectorAll('.header__featured-block-menu');
    var featuredItems = megaContent.querySelectorAll('.header__featured-block');

    // Menu sections → accordion each
    menuItems.forEach(function (item) {
      var level2Link = item.querySelector('.mega-menu__link--level-2');
      if (!level2Link) return;

      var subLinks = item.querySelectorAll('ul.list-unstyled a');

      if (subLinks.length > 0) {
        wrapper.appendChild(
          buildAccordion(level2Link.textContent.trim(), function (contentEl) {
            var ul = document.createElement('ul');
            ul.className = 'mobile-nav__sub-links';
            subLinks.forEach(function (link) {
              var li = document.createElement('li');
              var a = document.createElement('a');
              a.href = link.href;
              a.className = 'mobile-nav__sub-link';
              a.textContent = link.textContent.trim();
              li.appendChild(a);
              ul.appendChild(li);
            });
            contentEl.appendChild(ul);
          })
        );
      } else {
        // Section with no sub-links: render as plain link row
        var li = document.createElement('li');
        li.className = 'mobile-nav__item';
        var a = document.createElement('a');
        a.href = level2Link.href;
        a.className = 'mobile-nav__btn';
        a.textContent = level2Link.textContent.trim();
        li.appendChild(a);
        wrapper.appendChild(li);
      }
    });

    // Featured items
    if (featuredItems.length > 0) {
      var hasSections = menuItems.length > 0;

      if (featuredLabelText && hasSections) {
        // Group under an accordion labelled by featured_label
        wrapper.appendChild(
          buildAccordion(featuredLabelText, function (contentEl) {
            featuredItems.forEach(function (item) {
              contentEl.appendChild(buildFeaturedCard(item));
            });
          })
        );
      } else {
        // Show cards directly (no menu sections, e.g. DOVE TROVARCI)
        featuredItems.forEach(function (item) {
          wrapper.appendChild(buildFeaturedCard(item));
        });
      }
    }

    return wrapper;
  }

  function buildAccordion(labelText, populate) {
    var details = document.createElement('details');
    details.className = 'mobile-nav__accordion';

    var summary = document.createElement('summary');
    summary.className = 'mobile-nav__accordion-toggle';

    var span = document.createElement('span');
    span.textContent = labelText;
    summary.appendChild(span);
    summary.appendChild(chevronDown());
    details.appendChild(summary);

    var content = document.createElement('div');
    content.className = 'mobile-nav__accordion-content';
    populate(content);
    details.appendChild(content);

    return details;
  }

  function buildFeaturedCard(item) {
    var link = item.querySelector('a.mega-menu__link--featured');
    var titleEl = item.querySelector('.header__featured-block-title');
    var imgEl = item.querySelector('.header__featured-block-image');

    var card = document.createElement('div');
    card.className = 'mobile-nav__card';

    var inner = link ? document.createElement('a') : document.createElement('div');
    if (link) inner.href = link.href;
    inner.className = 'mobile-nav__card-inner';

    if (imgEl) inner.appendChild(imgEl.cloneNode(true));

    var text = document.createElement('div');
    text.className = 'mobile-nav__card-text';

    if (titleEl) {
      var title = document.createElement('span');
      title.className = 'mobile-nav__card-title';
      title.textContent = titleEl.textContent.trim();
      text.appendChild(title);
    }

    var cta = document.createElement('span');
    cta.className = 'mobile-nav__card-cta';
    cta.textContent = 'Scopri di più →';
    text.appendChild(cta);

    inner.appendChild(text);
    card.appendChild(inner);
    return card;
  }

  /* ── Screen transitions ────────────────────────────────────────────── */

  function openSubScreen(mainScreen, subScreen) {
    mainScreen.classList.add('is-hidden');
    subScreen.classList.add('is-active');
  }

  function returnToMain(container) {
    var mainScreen = container.querySelector('.mobile-nav__screen--main');
    var activeScreen = container.querySelector('.mobile-nav__screen--sub.is-active');
    if (activeScreen) activeScreen.classList.remove('is-active');
    if (mainScreen) mainScreen.classList.remove('is-hidden');
  }

  /* ── SVG helpers ───────────────────────────────────────────────────── */

  function chevronRight() {
    return svgIcon(
      '<path d="M1 1L5 5L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
      '0 0 6 10', 'mobile-nav__chevron'
    );
  }

  function chevronLeft() {
    return svgIcon(
      '<path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
      '0 0 8 14', 'mobile-nav__back-icon'
    );
  }

  function chevronDown() {
    return svgIcon(
      '<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
      '0 0 10 6', 'mobile-nav__accordion-caret'
    );
  }

  function svgIcon(path, viewBox, className) {
    var el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('viewBox', viewBox);
    el.setAttribute('fill', 'none');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('class', className);
    el.innerHTML = path;
    return el;
  }

  /* ── Boot ──────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();